import * as React from 'react';
import { HelpButton } from '@chess/api-help/react';
import { insets, lastRowOf, lastRowShift, rowColumnsOf, sectionColumns, tracksOf } from './layout.ts';

// A section of a dashboard: a group of cards under a title. A section that
// has a screen of its own is framed, and its header is the way in: the
// screen's rail icon and the title, and a compact "See all ›" at the right,
// both linking to the screen. A section without a screen is a plain title
// over its cards. Only the header links; the cards inside are links of
// their own. (Moved from home-section's HomeSection, 2026-10-10; the class
// names are the same.)

export interface DashboardSectionLink {
  /** The screen's address, for the anchors' href. */
  href: string;
  /** Opens it inside the app; the anchors call it instead of a page load. */
  open: () => void;
  /** Phosphor icon path data of the screen's rail entry, viewBox 0 0 256 256. */
  icon?: string;
  /** The link at the header's right; "See all" unless given. */
  label?: string;
}

// Phosphor CaretRight, regular weight.
const CARET_RIGHT = 'M181.66 133.66l-80 80a8 8 0 0 1-11.32-11.32L164.69 128 90.34 53.66a8 8 0 0 1 11.32-11.32l80 80a8 8 0 0 1 0 11.32Z';

export interface DashboardSectionViewProps {
  /** Absent for a section under a screen's own header, which needs none: no header is drawn. */
  title?: string;
  /** The screen this section stands for; absent for a section without one. */
  screen?: DashboardSectionLink;
  /** A help topic's id: its "?" after the title. */
  help?: string;
  /** At most this many cards in a row, the rest wrap; only ever fewer than the dashboards' 3 (`--dash-max-columns`). Absent: up to 3. */
  maxColumns?: number;
  /** A quiet label over the cards, within their width. */
  label?: string;
  /** The header's content in place of the title and the link: a composer's own heading. */
  head?: React.ReactNode;
  /** Content under the cards, capped and centered as the cards are, e.g. a table. */
  after?: React.ReactNode;
  children?: React.ReactNode;
}

const follow = (open: () => void) => (e: React.MouseEvent<HTMLAnchorElement>) => {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  open();
};

/** The cards laid out in the grid, looking through wrappers that draw no box (`display: contents`). */
export function cardsOf(el: Element): Element[] {
  const out: Element[] = [];
  for (const child of Array.from(el.children)) {
    if (getComputedStyle(child).display === 'contents') out.push(...cardsOf(child));
    else if (child.getBoundingClientRect().width > 0) out.push(child);
  }
  return out;
}

/**
 * Centers a wrapped last row under the rows above (a fourth card under
 * three): its cards keep their columns' width and are moved by half the room
 * the row leaves (layout.ts), with `translate`, which leaves the grid's
 * layout, and so the measuring, as it is. Every other card is left in place.
 */
function centerLastRow(body: Element, cards: Element[]) {
  const st = getComputedStyle(body);
  const tracks = tracksOf(st.gridTemplateColumns);
  const columns = tracks.length;
  const last = new Set(lastRowOf(cards.map((c) => c.getBoundingClientRect().top)));
  const used = cards.reduce((n, c, i) => {
    if (!last.has(i)) return n;
    const { gridColumnStart, gridColumnEnd } = getComputedStyle(c);
    return n + rowColumnsOf(gridColumnStart, gridColumnEnd, columns);
  }, 0);
  const shift = lastRowShift(columns, used, tracks[0] ?? 0, parseFloat(st.columnGap) || 0);
  cards.forEach((c, i) => {
    const style = (c as HTMLElement).style;
    if (!style) return;
    const value = shift && last.has(i) ? `${shift}px 0` : '';
    if (style.translate !== value) style.translate = value;
  });
}

/**
 * Tells the CSS how many columns the cards take, so a section with fewer
 * cards than its cap (3, or `maxColumns`) is capped and centered
 * (dashboard.css), centers a wrapped last row, and keeps the header within
 * the cards: it is inset to where the first card starts and the widest row
 * ends (layout.ts). Measured whenever the body resizes or its cards change.
 */
function useHeadWithinCards(section: React.RefObject<HTMLElement>, body: React.RefObject<HTMLElement>) {
  React.useLayoutEffect(() => {
    const s = section.current;
    const b = body.current;
    if (!s || !b || typeof ResizeObserver === 'undefined') return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const cards = cardsOf(b);
      const columns = sectionColumns(
        cards.map((c) => {
          const { gridColumnStart, gridColumnEnd } = getComputedStyle(c);
          return { start: gridColumnStart, end: gridColumnEnd };
        }),
      );
      // The CSS takes the least of this, `maxColumns` and --dash-max-columns.
      // Without cards (only content after them), or with only whole-row ones
      // (an empty line alone), nothing narrows the section: the content
      // takes the whole capped width, from the left.
      if (cards.length && columns > 0) s.style.setProperty('--home-section-columns', String(columns));
      else s.style.removeProperty('--home-section-columns');
      centerLastRow(b, cards);
      // Insets from the section's content box, which the header fills; the
      // body itself may be narrower and centered.
      const st = getComputedStyle(s);
      const r = s.getBoundingClientRect();
      const start = r.left + s.clientLeft + parseFloat(st.paddingLeft);
      const end = r.left + s.clientLeft + s.clientWidth - parseFloat(st.paddingRight);
      const inset = insets({ start, end }, cards.map((c) => c.getBoundingClientRect()));
      s.style.setProperty('--home-section-inset-start', `${inset.start}px`);
      s.style.setProperty('--home-section-inset-end', `${inset.end}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const resize = new ResizeObserver(schedule);
    resize.observe(b);
    resize.observe(s);
    const mutation = new MutationObserver(schedule);
    mutation.observe(b, { childList: true, subtree: true });
    update();
    return () => {
      resize.disconnect();
      mutation.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [section, body]);
}

export const DashboardSection: React.FC<DashboardSectionViewProps> = ({ title, screen, help, maxColumns, label, head, after, children }) => {
  const section = React.useRef<HTMLElement>(null);
  const body = React.useRef<HTMLDivElement>(null);
  useHeadWithinCards(section, body);
  const helpButton = help ? <HelpButton topic={help} /> : null;
  // The section's name for the click's `data-track-value`: its fixed title, lower-case ("openings", "middlegame", "ending").
  const trackValue = title?.toLowerCase();
  return (
    <section
      ref={section}
      className={`home-section${screen ? ' home-section-framed' : ''}`}
      aria-label={title}
      style={maxColumns ? ({ '--home-section-max-columns': maxColumns } as React.CSSProperties) : undefined}>
      {head !== undefined ? (
        <header className="home-section-head">{head}</header>
      ) : (
        (title || screen) && (
          <header className="home-section-head">
            {screen ? (
              <>
                <a className="home-section-title" href={screen.href} onClick={follow(screen.open)} data-track="dashboard.section.open" data-track-value={trackValue}>
                  {screen.icon && (
                    <svg className="home-section-icon" viewBox="0 0 256 256" aria-hidden="true">
                      <path d={screen.icon} />
                    </svg>
                  )}
                  <h2>{title}</h2>
                </a>
                {helpButton}
                <a
                  className="home-section-open"
                  href={screen.href}
                  onClick={follow(screen.open)}
                  data-track="dashboard.section.see-all"
                  data-track-value={trackValue}
                  aria-label={`Open the ${title} screen`}
                  title={`Open the ${title} screen`}>
                  {screen.label ?? 'See all'}
                  <svg className="home-section-caret" viewBox="0 0 256 256" aria-hidden="true">
                    <path d={CARET_RIGHT} />
                  </svg>
                </a>
              </>
            ) : (
              <>
                <h2 className="home-section-title">{title}</h2>
                {helpButton}
              </>
            )}
          </header>
        )
      )}
      {label && <p className="home-section-label">{label}</p>}
      <div ref={body} className="home-section-body">
        {children}
      </div>
      {after && <div className="home-section-after">{after}</div>}
    </section>
  );
};
